import type { MedicalResultHistory } from './hooks/useFetchMedicalResultHistories';
import { isDocumentExtractionV2Result } from '../../../../services/MedicalResultsService';

export const RECENCY_OPTIONS = [
  'all',
  'last_30_days',
  'last_60_days',
  'last_90_days',
  'last_180_days',
] as const;

export type Recency = (typeof RECENCY_OPTIONS)[number];

export function filterMedicalResultHistories(
  medicalResultHistories: MedicalResultHistory[],
  searchTerm: string,
  recency: Recency,
  annotatedOnly = false,
  extractedOnly = false,
) {
  return medicalResultHistories.flatMap(_ => {
    const results = extractedOnly
      ? _.medicalResultHistory.filter(isDocumentExtractionV2Result)
      : _.medicalResultHistory;

    if (extractedOnly && results.length === 0) {
      return [];
    }

    const label = (
      results[0]?.coding.display ?? _.dimension.name
    ).toLowerCase();
    const matchesSearchTerm = label.includes(searchTerm.toLowerCase());

    if (searchTerm && !matchesSearchTerm) {
      return [];
    }

    if (annotatedOnly && !results.some(r => r.annotation)) {
      return [];
    }

    const matchesRecency =
      recency === 'all' ||
      results.some(result => {
        const effectiveDateTime =
          result.resource.effectiveDateTime ||
          result.resource.effectivePeriod?.start ||
          result.resource.effectivePeriod?.end;

        if (!effectiveDateTime) {
          return false;
        }

        const effectiveDate = new Date(effectiveDateTime);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - effectiveDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        switch (recency) {
          case 'last_30_days':
            return diffDays <= 30;
          case 'last_60_days':
            return diffDays <= 60;
          case 'last_90_days':
            return diffDays <= 90;
          case 'last_180_days':
            return diffDays <= 180;
          default:
            return false;
        }
      });

    if (!matchesRecency) {
      return [];
    }

    return [
      extractedOnly
        ? {
            ..._,
            medicalResultHistory: results,
          }
        : _,
    ];
  });
}
